import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Card, Button, Table, Spinner, Modal, Form, Alert } from '@themesberg/react-bootstrap';
import { client } from "../../api/client";
import { fetchDrawConfigs } from "../../utils/draws";
import { Routes } from "../../routes";

export default () => {
  const history = useHistory();
  const [draws, setDraws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [viewMode, setViewMode] = useState("lister"); // ajouter, lister, resultat
  const [filterMode, setFilterMode] = useState("tout"); // actif, inactif, tout

  // Modal edit horaire state
  const [editModalDraw, setEditModalDraw] = useState(null);
  const [editOpenTime, setEditOpenTime] = useState("");
  const [editCloseTime, setEditCloseTime] = useState("");
  const [modalSaving, setModalSaving] = useState(false);
  const [modalError, setModalError] = useState("");

  const fetchDraws = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const list = await fetchDrawConfigs();
      setDraws(list);
      if (list.length === 0) {
        setLoadError("Aucun tirage chargé depuis l'API. Vérifiez le backend (port 8000) et votre connexion.");
      }
    } catch (err) {
      console.error("Failed to fetch draws:", err);
      setDraws([]);
      setLoadError("Erreur lors du chargement des tirages.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchDraws();
  }, [fetchDraws]);

  const handleToggleActive = async (drawId, currentStatus) => {
    const updated = draws.map(d => d.id === drawId ? { ...d, active: !d.active } : d);
    setDraws(updated);

    try {
      await client(`draw-configs/${drawId}/`, {
        method: "PUT",
        body: { is_active: !currentStatus }
      });
      fetchDraws();
    } catch (err) {
      console.error("Failed to update draw status on server:", err);
      const reverted = draws.map(d => d.id === drawId ? { ...d, active: currentStatus } : d);
      setDraws(reverted);
      alert("Erreur lors de la modification du statut : " + (err.error || err.message || JSON.stringify(err)));
    }
  };

  const handleOpenEditModal = (draw) => {
    setEditModalDraw(draw);
    setEditOpenTime(draw.open || draw.defaultOpen || "00:00");
    setEditCloseTime(draw.close || draw.defaultClose || "23:59");
    setModalError("");
  };

  const handleSaveHoraire = async (e) => {
    if (e) e.preventDefault();
    if (!editModalDraw) return;

    setModalError("");

    // Time comparison validation
    const sysClose = editModalDraw.defaultClose || "23:59";
    const sysOpen = editModalDraw.defaultOpen || "00:00";

    const editCloseNorm = editCloseTime.length === 5 ? `${editCloseTime}:00` : editCloseTime;
    const sysCloseNorm = sysClose.length === 5 ? `${sysClose}:00` : sysClose;
    const editOpenNorm = editOpenTime.length === 5 ? `${editOpenTime}:00` : editOpenTime;
    const sysOpenNorm = sysOpen.length === 5 ? `${sysOpen}:00` : sysOpen;

    if (editCloseNorm > sysCloseNorm) {
      setModalError(`Erreur : L'heure de fermeture (${editCloseTime}) ne peut pas dépasser l'heure système officielle (${sysClose}).`);
      return;
    }

    if (editOpenNorm < sysOpenNorm) {
      setModalError(`Erreur : L'heure d'ouverture (${editOpenTime}) ne peut pas être antérieure à l'heure système officielle (${sysOpen}).`);
      return;
    }

    setModalSaving(true);
    try {
      await client(`draw-configs/${editModalDraw.id}/`, {
        method: "PUT",
        body: {
          custom_open_time: editOpenTime || null,
          custom_close_time: editCloseTime || null
        }
      });
      setEditModalDraw(null);
      fetchDraws();
    } catch (err) {
      console.error("Failed to update draw times:", err);
      setModalError(err.error || err.detail || err.message || "Erreur lors de la mise à jour des horaires.");
    } finally {
      setModalSaving(false);
    }
  };

  const getFilteredDraws = () => {
    if (filterMode === "actif") return draws.filter(d => d.active);
    if (filterMode === "inactif") return draws.filter(d => !d.active);
    return draws;
  };

  const filtered = getFilteredDraws();

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Header Tabs */}
      <div className="d-flex mb-4" style={{ gap: '10px' }}>
        <Button 
          variant={viewMode === "ajouter" ? "primary" : "outline-primary"}
          style={{
            borderRadius: '4px',
            fontSize: '0.9rem',
            padding: '6px 20px',
            fontWeight: '500',
            color: viewMode === "ajouter" ? '#FFF' : undefined
          }}
          onClick={() => setViewMode("ajouter")}
        >
          Ajouter
        </Button>
        <Button 
          variant={viewMode === "lister" ? "success" : "outline-success"}
          style={{
            borderRadius: '4px',
            fontSize: '0.9rem',
            padding: '6px 20px',
            fontWeight: '500',
            color: viewMode === "lister" ? '#FFF' : undefined
          }}
          onClick={() => setViewMode("lister")}
        >
          Lister
        </Button>
        <Button 
          variant={viewMode === "resultat" ? "primary" : "outline-primary"}
          style={{
            borderRadius: '4px',
            fontSize: '0.9rem',
            padding: '6px 20px',
            fontWeight: '500',
            color: viewMode === "resultat" ? '#FFF' : undefined
          }}
          onClick={() => setViewMode("resultat")}
        >
          Resultat lotterie
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="text-muted small mt-2">Chargement des tirages...</div>
        </div>
      ) : viewMode === "lister" ? (
        <Card className="border-0 shadow-sm" style={{ borderRadius: '8px' }}>
          <Card.Body className="p-4">
            <h5 className="mb-3" style={{ fontWeight: 'bold', color: '#333' }}>
              Liste des Tirages disponible
            </h5>
            {loadError && (
              <div className="alert alert-warning py-2 small mb-3">{loadError}</div>
            )}

            {/* Actif, Inactif, Tout filter buttons aligned cleanly */}
            <div className="d-flex mb-4" style={{ gap: '8px' }}>
              <Button
                style={{
                  backgroundColor: filterMode === "actif" ? '#28A745' : 'transparent',
                  borderColor: '#28A745',
                  color: filterMode === "actif" ? '#FFF' : '#28A745',
                  fontSize: '0.82rem',
                  padding: '4px 14px',
                  borderRadius: '4px'
                }}
                onClick={() => setFilterMode("actif")}
              >
                Actif
              </Button>
              <Button
                style={{
                  backgroundColor: filterMode === "inactif" ? '#FFC107' : 'transparent',
                  borderColor: '#FFC107',
                  color: filterMode === "inactif" ? '#222' : '#FFC107',
                  fontSize: '0.82rem',
                  padding: '4px 14px',
                  borderRadius: '4px'
                }}
                onClick={() => setFilterMode("inactif")}
              >
                Inactif
              </Button>
              <Button
                style={{
                  backgroundColor: filterMode === "tout" ? '#007BFF' : 'transparent',
                  borderColor: '#007BFF',
                  color: filterMode === "tout" ? '#FFF' : '#007BFF',
                  fontSize: '0.82rem',
                  padding: '4px 14px',
                  borderRadius: '4px'
                }}
                onClick={() => setFilterMode("tout")}
              >
                Tout
              </Button>
            </div>

            {/* Draws table */}
            <div className="table-responsive">
              <Table bordered hover className="align-items-center mb-0" style={{ borderCollapse: 'collapse', borderColor: '#EAEAEA' }}>
                <thead>
                  <tr style={{ backgroundColor: '#FAF9F9' }}>
                    <th style={{ padding: '12px 16px', fontSize: '0.88rem', color: '#111', fontWeight: 'bold' }}>Prime</th>
                    <th style={{ padding: '12px 16px', fontSize: '0.88rem', color: '#111', fontWeight: 'bold' }}>Limite</th>
                    <th style={{ padding: '12px 16px', fontSize: '0.88rem', color: '#111', fontWeight: 'bold' }}>Horaire</th>
                    <th style={{ padding: '12px 16px', fontSize: '0.88rem', color: '#111', fontWeight: 'bold' }}>Nom</th>
                    <th style={{ padding: '12px 16px', fontSize: '0.88rem', color: '#111', fontWeight: 'bold' }}>Ouverture</th>
                    <th style={{ padding: '12px 16px', fontSize: '0.88rem', color: '#111', fontWeight: 'bold' }}>Fermeture</th>
                    <th style={{ padding: '12px 16px', fontSize: '0.88rem', color: '#111', fontWeight: 'bold' }}>Statut</th>
                    <th style={{ padding: '12px 16px', fontSize: '0.88rem', color: '#111', fontWeight: 'bold' }}>Actif</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((draw) => {
                    // Status logic: Green (#28A745) if Open, Red (#DC3545) if Closed
                    const isDrawOpen = draw.isOpen || (draw.active && draw.status === "Ouvert");
                    const statusColor = isDrawOpen ? '#28A745' : '#DC3545';
                    const displayStatus = isDrawOpen ? "Ouvert" : "Fermé";

                    return (
                      <tr key={draw.id} style={{ verticalAlign: 'middle' }}>
                        {/* Modifier Prime */}
                        <td style={{ padding: '10px 16px' }}>
                          <Button 
                            style={{
                              backgroundColor: '#007BFF',
                              borderColor: '#007BFF',
                              color: '#FFF',
                              fontSize: '0.59rem',
                              padding: '3px 8px',
                              borderRadius: '3px'
                            }}
                            onClick={() => history.push(`${Routes.Primes.path}?tab=par_tirage&draw=${draw.id}`)}
                          >
                            Modifier Prime
                          </Button>
                        </td>

                        {/* Modifier limite */}
                        <td style={{ padding: '10px 16px' }}>
                          <Button 
                            style={{
                              backgroundColor: '#007BFF',
                              borderColor: '#007BFF',
                              color: '#FFF',
                              fontSize: '0.59rem',
                              padding: '3px 8px',
                              borderRadius: '3px'
                            }}
                            onClick={() => history.push(`${Routes.LimiteBoule.path}?tab=tirage&draw=${draw.id}`)}
                          >
                            Modifier limite
                          </Button>
                        </td>

                        {/* Modifier Horaire / Fermeture */}
                        <td style={{ padding: '10px 16px' }}>
                          <Button 
                            style={{
                              backgroundColor: '#6C757D',
                              borderColor: '#6C757D',
                              color: '#FFF',
                              fontSize: '0.59rem',
                              padding: '3px 8px',
                              borderRadius: '3px'
                            }}
                            onClick={() => handleOpenEditModal(draw)}
                          >
                            Horaire
                          </Button>
                        </td>

                        {/* Nom in statusColor (Green if Open, Red if Closed) - Normal weight */}
                        <td style={{ padding: '10px 16px', fontSize: '0.88rem', color: statusColor, fontWeight: 'normal' }}>
                          {draw.name}
                        </td>

                        {/* Ouverture in statusColor */}
                        <td style={{ padding: '10px 16px', fontSize: '0.88rem', color: statusColor, fontWeight: 'normal' }}>
                          {draw.open}
                        </td>

                        {/* Fermeture in statusColor */}
                        <td style={{ padding: '10px 16px', fontSize: '0.88rem', color: statusColor, fontWeight: 'normal' }}>
                          {draw.close} {draw.hasCustomClose && <span className="small text-muted font-normal">(perso)</span>}
                        </td>

                        {/* Statut in statusColor (Ouvert in Green, Fermé in Red) */}
                        <td style={{ padding: '10px 16px', fontSize: '0.88rem', color: statusColor, fontWeight: 'normal' }}>
                          {displayStatus}
                        </td>

                        {/* Actif column */}
                        <td style={{ padding: '10px 16px' }}>
                          <div className="d-flex flex-column align-items-center">
                            <span style={{ 
                              fontSize: '0.88rem', 
                              color: draw.active ? '#28A745' : '#DC3545', 
                              fontWeight: '500', 
                              marginBottom: '3px' 
                            }}>
                              {draw.active ? "oui" : "non"}
                            </span>
                            <Button 
                              style={{
                                backgroundColor: draw.active ? '#FFC107' : '#28A745',
                                borderColor: draw.active ? '#FFC107' : '#28A745',
                                color: draw.active ? '#222' : '#FFF',
                                fontSize: '0.54rem',
                                padding: '2px 6px',
                                borderRadius: '3px'
                              }}
                              onClick={() => handleToggleActive(draw.id, draw.active)}
                            >
                              {draw.active ? "Desactivé" : "Activé"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm" style={{ borderRadius: '8px' }}>
          <Card.Body className="p-4 text-center">
            <h5>Vue {viewMode} non implémentée</h5>
          </Card.Body>
        </Card>
      )}

      {/* Modal Modifier Horaire */}
      <Modal show={Boolean(editModalDraw)} onHide={() => setEditModalDraw(null)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="h5 fw-bold" style={{ color: '#333' }}>
            Modifier les horaires — {editModalDraw?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          {modalError && (
            <Alert variant="danger" className="py-2 px-3 small mb-3">
              {modalError}
            </Alert>
          )}

          <div className="mb-3 p-3 bg-light rounded" style={{ fontSize: '0.85rem' }}>
            <div><strong>Heure système officielle (ouverture min) :</strong> <span className="text-success fw-bold">{editModalDraw?.defaultOpen}</span></div>
            <div><strong>Heure système officielle (fermeture max) :</strong> <span className="text-danger fw-bold">{editModalDraw?.defaultClose}</span></div>
            <div className="text-muted mt-1">Vous pouvez adapter vos horaires entre ces deux bornes système officielles.</div>
          </div>

          <Form onSubmit={handleSaveHoraire}>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold text-dark">Heure d'ouverture</Form.Label>
              <Form.Control 
                type="time"
                value={editOpenTime}
                onChange={(e) => setEditOpenTime(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold text-dark">Heure de fermeture personnalisée</Form.Label>
              <Form.Control 
                type="time"
                value={editCloseTime}
                onChange={(e) => setEditCloseTime(e.target.value)}
              />
              <Form.Text className="text-muted" style={{ fontSize: '0.78rem' }}>
                Maximum autorisé : <strong>{editModalDraw?.defaultClose}</strong>
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button variant="secondary" onClick={() => setEditModalDraw(null)}>
                Annuler
              </Button>
              <Button type="submit" variant="primary" disabled={modalSaving}>
                {modalSaving ? <Spinner animation="border" size="sm" /> : "Enregistrer"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};
